import React, {useEffect} from 'react';
import {floor} from 'lodash';
import moment, {Moment, MomentInput} from 'moment';
import {isSameDay} from 'date-fns';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {
  contributeFieldType,
  contributeFromBase,
  contributeFromType,
  contributeDestinationValuesType,
} from '../BuilderContribute/builderContributeFields';
import FREQUENCY from '../../../constants/Frequency';
import {ComboFrequencyEnum} from '../../../schema-types/globalTypes';
import {
  AutoFieldHelpers,
  SetFieldValueWithSourceKey,
} from '../../../components/AutoForm/useAutoForm';
import {UPDATES} from '../../../shared/UPDATES';
import {OvbuilderDetails_ovrecommendDetails} from '../../../schema-types/OvbuilderDetails';
import {moneyFieldValueType} from '../../../components/AutoForm/AutoFormNew';
import {BuilderPot, BuilderPot_dentistChildren} from '../../../schema-types/BuilderPot';
import {getCurrencyFloat} from '../../../utils/currencyFormatter';
import {
  optionalRound,
  findParentOfChild,
} from '../CreateBuilder/BuilderSourceDestination/builderUtils';
import {getFeeFormikFromServer} from '../CreateBuilder/BuilderDAInitialFees';
import saltus from '../../../shared/logger';
import getMinDate from '../../../utils/getMinDate';
import loadMomentDate from '../../../utils/loadMomentDate';
import {newUTCDate} from '../../../utils/newUTCDate';

// ============================================
// BuilderContributeSources.tsx hooks (270-290)
// ============================================

export const setContributeFullAmount = ({
  full,
  maxAmount,
  contributeFromClientHelpers,
}: {
  full: boolean | undefined;
  maxAmount: moneyFieldValueType;
  contributeFromClientHelpers: AutoFieldHelpers<contributeFromType['singleClientAmount']>;
}) => {
  if (full) {
    contributeFromClientHelpers.setValue(UPDATES._D288, String(maxAmount));
  }
};

export const calculateNetSingleClient = ({
  singleClientAmount,
  isCash,
}: {
  singleClientAmount: moneyFieldValueType;
  isCash: boolean | undefined | null;
}): number | undefined => {
  try {
    if (singleClientAmount && !isCash) {
      const number = Number(singleClientAmount);
      return floor(number * 0.8, 2);
    }
  } catch (err) {
    // do nothing
  }
  return undefined;
};

export const calculateNetRecurringClient = ({
  recurringClientAmount,
  isCash,
}: {
  recurringClientAmount: moneyFieldValueType;
  isCash: boolean | undefined | null;
}): number | undefined => {
  try {
    if (recurringClientAmount && !isCash) {
      const number = Number(recurringClientAmount);
      return floor(number * 0.8, 2);
    }
  } catch (err) {
    // do nothing
  }
  return undefined;
};

export const useSetContributeFullAmount = ({
  contributeFromFieldObj,
  maxAmount,
  contributeFromClientHelpers,
}: {
  contributeFromFieldObj: {value: contributeFieldType['contributeFrom'] | undefined};
  maxAmount: moneyFieldValueType;
  contributeFromClientHelpers: AutoFieldHelpers<contributeFromType['singleClientAmount']>;
}) => {
  // `full` only exists on plan sources; cash and bank sources have no
  // source-pot balance to redeem in full.
  const full =
    contributeFromFieldObj.value?.sourceType === 'plan'
      ? contributeFromFieldObj.value.full
      : undefined;
  useEffect(() => {
    setContributeFullAmount({
      full,
      maxAmount,
      contributeFromClientHelpers,
    });
  }, [full]);
};

export const useCalculateNetSingleClient = ({
  contributeFromFieldObj,
  isCash,
  contributeFromNetHelpers,
}: {
  contributeFromFieldObj: {value: contributeFieldType['contributeFrom'] | undefined};
  isCash: boolean | undefined | null;
  contributeFromNetHelpers: AutoFieldHelpers<contributeFromType['netSingleClientAmount']>;
}) => {
  useEffect(() => {
    const netValue = calculateNetSingleClient({
      singleClientAmount: contributeFromFieldObj?.value?.singleClientAmount,
      isCash,
    });
    if (netValue !== undefined) {
      contributeFromNetHelpers.setValue(UPDATES._D278, netValue);
    }
  }, [contributeFromFieldObj?.value?.singleClientAmount, isCash]);
};

export const useCalculateNetRecurringClient = ({
  contributeFromFieldObj,
  isCash,
  contributeFromNetRecurringHelpers,
}: {
  contributeFromFieldObj: {value: contributeFieldType['contributeFrom'] | undefined};
  isCash: boolean | undefined | null;
  contributeFromNetRecurringHelpers: AutoFieldHelpers<
    contributeFromType['netSingleClientAmount']
  >;
}) => {
  useEffect(() => {
    const netValue = calculateNetRecurringClient({
      recurringClientAmount: contributeFromFieldObj?.value?.recurringClientAmount,
      isCash,
    });
    if (netValue !== undefined) {
      contributeFromNetRecurringHelpers.setValue(UPDATES._D290, netValue);
    }
  }, [contributeFromFieldObj?.value?.recurringClientAmount, isCash]);
};

export const setMinimumDateMustBe3WeeksFromToday = ({
  contributeValues,
  setFieldValue,
  setPreviousDate,
  previousDate,
  today, // need to pass this in for testing
}: {
  contributeValues: contributeFieldType | undefined;
  setFieldValue: SetFieldValueWithSourceKey<builderFieldsType>;
  setPreviousDate: React.Dispatch<React.SetStateAction<string>>;
  previousDate: string;
  today: MomentInput;
}) => {
  if (
    comboToFrequencyMapping[
      contributeValues?.contributeFrom?.comboFrequency ?? ComboFrequencyEnum.SINGLE
    ] === FREQUENCY.SINGLE ||
    !contributeValues?.contributeFrom
  ) {
    return;
  }
  const minimumDate = getMinDate({
    today,
    dateValue: contributeValues?.contributeFrom?.start as Moment,
    daysOffset: 15,
    isBusiness: true,
  });
  if (minimumDate.date() === 1 || minimumDate.date() === 15) {
    // do nothing
  } else if (minimumDate.date() < 15) {
    minimumDate.set('date', 15);
  } else {
    minimumDate.add(1, 'month');
    minimumDate.startOf('month');
  }
  if (!contributeValues.contributeFrom.start) {
    // upon frequency is anything that isn't single, set start date to 3 weeks in advance if not there
    setFieldValue(UPDATES._D268, 'contribute.contributeFrom.start', minimumDate);
    return;
  }

  // this prevents an infinite loop and the value is only set when the date and previous date differ
  if (
    isSameDay(
      moment.utc(contributeValues.contributeFrom?.start).toDate(),
      newUTCDate(previousDate)
    )
  ) {
    return;
  }
  if (moment.utc(contributeValues.contributeFrom?.start).isSame(minimumDate, 'date')) {
    return;
  }
  setFieldValue(UPDATES._D269, 'contribute.contributeFrom.start', minimumDate);
  setPreviousDate(minimumDate.toISOString()?.split('T')?.[0] || '');
};

// ============================================
// BuilderContributeHelpers.tsx (291-293)
// ============================================

// Fields every fresh source selection starts from.
export const emptyContributeFromBase: contributeFromBase = {
  comboFrequency: ComboFrequencyEnum.SINGLE,
  singleClientAmount: undefined,
  recurringClientAmount: undefined,
  initialFee: null,
  legacyInitialFee: null,
  legacyInitialEmployerFee: null,
  sourceOfFunds: null,
  employerSourceOfFunds: null,
};

export const changeContributeFrom = ({
  pot,
  parentPotId,
}: {
  pot: BuilderPot | BuilderPot_dentistChildren;
  parentPotId?: string;
}): {
  contributeFrom: contributeFromType;
  pot: BuilderPot | BuilderPot_dentistChildren;
} => {
  return {
    contributeFrom: {
      ...emptyContributeFromBase,
      sourceType: 'plan',
      id: pot.id,
      parentPotId,
      maxAmount: 'totalValue' in pot ? getCurrencyFloat(pot.totalValue) : 0,
    },
    pot,
  };
};

export const changeContributeFromPlan = ({
  contributeFromPots,
  contributeFromHelpers,
}: {
  contributeFromPots: BuilderPot[];
  contributeFromHelpers: AutoFieldHelpers<contributeFromType | undefined>;
}) => {
  return (value: string) => {
    const parent = findParentOfChild(contributeFromPots, value);
    if (parent) {
      const child = parent.dentistChildren?.find((c) => c.id === value);
      if (!child) return;
      const {contributeFrom} = changeContributeFrom({pot: child, parentPotId: parent.id});
      contributeFromHelpers.setValue(UPDATES._D292, contributeFrom);
      return;
    }
    // Leaf pot (a pot with no dentist children) — use the pot directly
    const leafPot = contributeFromPots.find((p) => p.id === value);
    if (!leafPot) return;
    const {contributeFrom} = changeContributeFrom({
      pot: leafPot,
      parentPotId: undefined,
    });
    contributeFromHelpers.setValue(UPDATES._D292, contributeFrom);
  };
};

// Selecting a bank account means a new cash contribution funded by it (DBO-1439).
export const changeContributeFromBankAccount = ({
  contributeFromHelpers,
}: {
  contributeFromHelpers: AutoFieldHelpers<contributeFromType | undefined>;
}) => {
  return (bankAccountId: string) => {
    contributeFromHelpers.setValue(UPDATES._D294, {
      ...emptyContributeFromBase,
      sourceType: 'bankAccount',
      id: bankAccountId,
    });
  };
};

// Selecting an employer bank account means new cash funded by the employer.
export const changeContributeFromEmployerBankAccount = ({
  contributeFromHelpers,
}: {
  contributeFromHelpers: AutoFieldHelpers<contributeFromType | undefined>;
}) => {
  return (bankAccountId: string) => {
    contributeFromHelpers.setValue(UPDATES._D294, {
      ...emptyContributeFromBase,
      sourceType: 'employerBankAccount',
      id: bankAccountId,
    });
  };
};

export const getAllContributePots = ({
  sourcePots,
  destinationPots,
  builderDetails,
}: {
  sourcePots: BuilderPot[];
  destinationPots: BuilderPot[];
  builderDetails?: OvbuilderDetails_ovrecommendDetails | null;
}): BuilderPot[] => {
  const allPots = [...destinationPots, ...sourcePots];
  const destinationPot = builderDetails?.contribute?.destinationPot;
  if (destinationPot) {
    allPots.push(destinationPot);
  }
  return allPots;
};

export const getContributeDestinationFromSelection = ({
  pot,
}: {
  pot: BuilderPot | BuilderPot_dentistChildren;
}): contributeDestinationValuesType => {
  if ('dentistChildren' in pot) {
    return {existingPotId: null, parentPotId: pot.id};
  }
  return {existingPotId: pot.id, parentPotId: pot.parent?.id ?? undefined};
};

export const loadSavedContribution = ({
  builderDetails,
  isSPPDA,
}: {
  builderDetails: OvbuilderDetails_ovrecommendDetails | null;
  isSPPDA: boolean;
}): contributeFieldType | undefined => {
  if (!builderDetails) return undefined;
  const {contribute} = builderDetails;

  const initialFee = contribute?.initialFee?.id ?? null;
  const legacyInitialFee = getFeeFormikFromServer(
    isSPPDA,
    contribute?.sppInitialFee ?? null
  );
  const initialEmployerFee = contribute?.initialEmployerFee?.id ?? null;
  const legacyInitialEmployerFee = getFeeFormikFromServer(
    isSPPDA,
    contribute?.sppInitialEmployerFee ?? null
  );

  const {comboFrequency} = getContributionFrequencies(builderDetails);

  const base: contributeFromBase = {
    comboFrequency,
    start: loadMomentDate(builderDetails.startDate) as Moment,
    singleClientAmount: optionalRound(contribute?.singleClientAmount) || '',
    recurringClientAmount: optionalRound(contribute?.recurringClientAmount) || '',
    recurringEmployerAmount: optionalRound(contribute?.recurringEmployerAmount) || '',
    singleEmployerAmount: optionalRound(contribute?.singleEmployerAmount) || '',
    initialFee,
    initialEmployerFee,
    legacyInitialFee,
    legacyInitialEmployerFee,
    sourceOfFunds: contribute?.sourceOfFunds ?? null,
    employerSourceOfFunds: contribute?.employerSourceOfFunds ?? null,
  };

  // ---- contributeFrom ----
  // The saved source union maps 1:1 onto the form-state variants.
  const source = contribute?.source;
  const employerSource = contribute?.employerSourceBankAccount;
  let contributeFrom: contributeFromType;
  if (source?.__typename === 'BankAccountType') {
    contributeFrom = {...base, sourceType: 'bankAccount', id: source.id};
  } else if (employerSource && source?.__typename !== 'PotType') {
    // Employer money is saved as a dummy cash pot plus the employer's bank
    // account, so the employer account is what identifies the source row.
    contributeFrom = {...base, sourceType: 'employerBankAccount', id: employerSource.id};
  } else if (source?.__typename === 'PotType') {
    contributeFrom = {
      ...base,
      sourceType: 'plan',
      id: source.id,
      maxAmount: getCurrencyFloat(source.totalValue),
      inSpecie: contribute?.isInSpecie ?? Boolean(source.inSpecie),
    };
  } else {
    // CashPotType (or nothing saved yet) — plain new cash.
    contributeFrom = {...base, sourceType: 'newCash', cashPotId: source?.id};
  }

  // ---- destinationValues ----
  // contribute.destinationPot is the saved leaf/child pot directly.
  // Its parent (wrapper) id comes from destinationPot.parent?.id, or the pot itself if top-level.
  const savedDestinationPot = contribute?.destinationPot;
  const existingPotId = savedDestinationPot?.id ?? null;
  const parentPotId = savedDestinationPot?.parent?.id ?? undefined;

  const destinationValues: contributeDestinationValuesType = {
    existingPotId,
    parentPotId,
  };

  // contributeTo is the wrapper pot id (parent if exists, else the leaf itself)
  const contributeTo = parentPotId ?? existingPotId ?? undefined;

  const newContribute: contributeFieldType = {
    contributeTo,
    destinationValues,
    contributeFrom,
  };
  saltus.log({newContribute});
  return newContribute;
};

// ============================================
// CONTRIBUTE FREQUENCY HELPERS
// ============================================

export const comboToFrequencyMapping: Record<ComboFrequencyEnum, FREQUENCY> = {
  [ComboFrequencyEnum.ANNUAL]: FREQUENCY.ANNUAL,
  [ComboFrequencyEnum.ANNUAL_AND_SINGLE]: FREQUENCY.ANNUAL,
  [ComboFrequencyEnum.MONTHLY]: FREQUENCY.MONTHLY,
  [ComboFrequencyEnum.MONTHLY_AND_SINGLE]: FREQUENCY.MONTHLY,
  [ComboFrequencyEnum.QUARTERLY]: FREQUENCY.QUARTERLY,
  [ComboFrequencyEnum.QUARTERLY_AND_SINGLE]: FREQUENCY.QUARTERLY,
  [ComboFrequencyEnum.SINGLE]: FREQUENCY.SINGLE,
};

const frequencyWithSingleToCombo: Record<FREQUENCY, ComboFrequencyEnum> = {
  [FREQUENCY.ANNUAL]: ComboFrequencyEnum.ANNUAL_AND_SINGLE,
  [FREQUENCY.MONTHLY]: ComboFrequencyEnum.MONTHLY_AND_SINGLE,
  [FREQUENCY.QUARTERLY]: ComboFrequencyEnum.QUARTERLY_AND_SINGLE,
  [FREQUENCY.SINGLE]: ComboFrequencyEnum.SINGLE,
};

const recurringFrequencyToCombo: Record<FREQUENCY, ComboFrequencyEnum> = {
  [FREQUENCY.ANNUAL]: ComboFrequencyEnum.ANNUAL,
  [FREQUENCY.MONTHLY]: ComboFrequencyEnum.MONTHLY,
  [FREQUENCY.QUARTERLY]: ComboFrequencyEnum.QUARTERLY,
  [FREQUENCY.SINGLE]: ComboFrequencyEnum.SINGLE,
};

// The server returns RecurringFrequencyEnum values (ALL_CAPS e.g. 'MONTHLY') but
// the lookup tables are keyed on FREQUENCY values (Title Case e.g. 'Monthly').
// This normalises the server value to a FREQUENCY enum value.
const normaliseServerFrequency = (
  freq: string | null | undefined
): FREQUENCY | undefined => {
  if (!freq) return undefined;
  const upper = freq.toUpperCase();
  if (upper === 'MONTHLY') return FREQUENCY.MONTHLY;
  if (upper === 'QUARTERLY') return FREQUENCY.QUARTERLY;
  if (upper === 'ANNUAL') return FREQUENCY.ANNUAL;
  if (upper === 'SINGLE') return FREQUENCY.SINGLE;
  return undefined;
};

export const getContributionFrequencies = (
  builderDetails: OvbuilderDetails_ovrecommendDetails | null
): {comboFrequency: ComboFrequencyEnum; frequency: FREQUENCY} => {
  if (!builderDetails || !builderDetails.action) {
    return {comboFrequency: ComboFrequencyEnum.SINGLE, frequency: FREQUENCY.SINGLE};
  }
  const comboFrequency =
    builderDetails.contribute?.comboFrequency ?? ComboFrequencyEnum.SINGLE;
  const frequency = comboToFrequencyMapping[comboFrequency];
  return {comboFrequency, frequency};
};

// ============================================
// BuilderContribute.tsx — initialValueOverrides approach (replaces loadSavedContribution)
// ============================================

export const getContributeInitialValues = (
  builderDetails: OvbuilderDetails_ovrecommendDetails | null,
  isSPPDA: boolean
): contributeFieldType | undefined => {
  if (!builderDetails?.contribute)
    return {
      contributeTo: null,
      destinationValues: {
        existingPotId: null,
      },
    };
  return loadSavedContribution({builderDetails, isSPPDA});
};
